/**
 * Payment-details read hook (task 629 — Settings → Payment page).
 *
 * Reads a read-only projection of the account's Stripe payment method and
 * customer billing details. This is the FRONTEND half of the contract defined
 * by task 627 ("AMS Stripe Read APIs"): AMS exposes the account-scoped read and
 * innovate-service proxies it, owner-gated, under `/api/billing/payment-details`
 * in the standard `{ success, data }` wrapper. Every EDIT still deep-links to
 * the Stripe Billing Portal — no card data beyond brand/last4/expiry is ever
 * returned, and no card form renders in Innovate.
 *
 * WHY this lives in its own hook file rather than `services/api.ts`:
 *   - Task 627 (the backend endpoint) is not yet merged, so this fetches against
 *     the *intended* contract and fails closed (see below) until it ships.
 *   - Task 628 (the Subscription page) is being built concurrently and also
 *     touches billing reads; keeping this call out of the shared `services/api.ts`
 *     avoids a merge collision. When task 627 lands, the raw call below should be
 *     folded into a `billingApi.getPaymentDetails()` in `services/api.ts`.
 *
 * Failure posture: the endpoint may be absent (404), disabled by its release
 * flag / non-owner (403), or unimplemented (501). None of those are error states
 * for the User — the page degrades to its "payment details unavailable" note.
 * The hook therefore resolves such responses to `null` rather than throwing.
 */

import { useQuery } from '@tanstack/react-query';
import api from '@/services/api';

/** One Stripe customer tax ID (e.g. `{ type: 'ca_gst_hst', value: '123456789 RT0001' }`). */
export interface PaymentTaxId {
  type: string;
  value: string;
}

/** The billed-to address on the Stripe customer, any field of which may be absent. */
export interface PaymentBillingAddress {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
}

/** Card summary — the only payment-method data ever exposed (brand/last4/expiry). */
export interface PaymentMethodSummary {
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
}

/** Stripe customer billing details that appear on every invoice. */
export interface PaymentBillingDetails {
  email: string | null;
  name: string | null;
  address: PaymentBillingAddress | null;
  taxIds: PaymentTaxId[];
}

/**
 * The payment-details projection.
 *   - `hasCustomer: false` → the account has no Stripe customer yet (new or
 *     no-plan account); the page shows the choose-a-plan empty state.
 *   - `paymentMethod: null` with `hasCustomer: true` → a customer exists but no
 *     card is on file; the page shows the "no card on file" state.
 */
export interface PaymentDetails {
  hasCustomer: boolean;
  paymentMethod: PaymentMethodSummary | null;
  billingDetails: PaymentBillingDetails | null;
  currency: string | null;
}

/** Standard wrapper shape returned by the innovate-service proxy. */
interface PaymentDetailsResponse {
  success: boolean;
  data: PaymentDetails;
}

/** Status codes that mean "not available yet", degraded gracefully rather than surfaced as errors. */
const GRACEFUL_STATUSES = new Set([403, 404, 501]);

export const PAYMENT_DETAILS_QUERY_KEY = ['billing', 'payment-details'] as const;

/**
 * Fetch the account's payment method + billing details.
 *
 * `enabled` lets the page skip the call entirely for a non-owner (who never sees
 * this data). Resolves to `null` when the endpoint is absent/disabled so the
 * caller renders the unavailable note instead of an error; other failures (a 5xx,
 * a transport error) are allowed to reject so React Query surfaces `isError`.
 */
export const usePaymentDetails = (enabled: boolean = true) =>
  useQuery<PaymentDetails | null>({
    queryKey: PAYMENT_DETAILS_QUERY_KEY,
    enabled,
    staleTime: 60_000,
    retry: 1,
    queryFn: async () => {
      try {
        const res = await api.get<PaymentDetailsResponse>('/billing/payment-details');
        return res.data.data;
      } catch (err: unknown) {
        const status = (err as { status?: number })?.status;
        if (status !== undefined && GRACEFUL_STATUSES.has(status)) {
          // Endpoint not deployed / disabled / not owner — treat as absent.
          return null;
        }
        throw err;
      }
    },
  });
